import React, { useEffect, useState, useRef } from 'react';
import moment from 'moment';
import './index.scss';
import type { ReviewItem, QuarterlyReviewProgressProps } from './types';
import { getStatusInfo } from './constants';
import { Input } from 'tea-component';
import COSFileList from '@sg/components/COSFileList';
import UploadFileList from '@sg/components/UploadFileList';
import TinyEditor from '@sg/components/TinyEditor';
import { renderRichText } from '@sg/utils/commonPageMethod';

const QuarterlyReviewProgress: React.FC<QuarterlyReviewProgressProps> = ({ 
  data, 
  isEditing = false,
  canEdit = false,
  onChange,
  selectedProjectId
}) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const hasAutoScrolledRef = useRef(false);
  const [containerWidth, setContainerWidth] = useState(0);
  
  // State for edit values - stores description changes for each review
  const [editValues, setEditValues] = useState<Record<number, string>>({});
  
  // Handler for description change
  const handleDescriptionChange = (reviewId: number, value: string) => {
    const newEditValues = { ...editValues, [reviewId]: value };
    setEditValues(newEditValues);
    
    // Call onChange callback with updated data
    if (onChange) {
      const updatedData = data.map(review => 
        review.id === reviewId ? { ...review, description: value } : review
      );
      onChange(updatedData);
    }
  };
  
  const handleFilesUpload = (reviewId: number, updatedFiles: any) => {
    console.log('handleFilesUpload called:', { reviewId, updatedFiles });
    
    if (onChange) {
      const updatedData = data.map(review => {
        if (review.id === reviewId) {
          // UploadFileList with defaultList manages all files (old + new)
          // So we can directly use the updatedFiles it returns
          const allFiles = Array.isArray(updatedFiles) ? updatedFiles : [];
          
          // Check if files actually changed to prevent infinite loop
          if (JSON.stringify(review.files) === JSON.stringify(allFiles)) {
            console.log('Files unchanged, skipping update');
            return review;
          }
          
          console.log('Updating review files:', { reviewId, oldFiles: review.files, allFiles });
          return { ...review, files: allFiles };
        }
        return review;
      });
      
      console.log('Calling onChange with updated data:', updatedData);
      onChange(updatedData);
    }
  };

  // Clear edit values when exiting edit mode
  useEffect(() => {
    if (!isEditing) {
      setEditValues({});
    }
  }, [isEditing]);

  useEffect(() => {
    if (!containerRef.current) return;
    
    let rafId: number | null = null;
    
    const updateWidth = () => {
      if (containerRef.current) {
        const width = containerRef.current.offsetWidth;
        console.log('Updating container width:', width);
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    };
    
    // Use ResizeObserver to detect when element gets its width
    // Wrap setState in requestAnimationFrame to prevent "ResizeObserver loop completed" error
    const resizeObserver = new ResizeObserver((entries) => {
      // Cancel any pending animation frame
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      
      // Schedule state update for next animation frame
      rafId = requestAnimationFrame(() => {
        for (const entry of entries) {
          const width = entry.contentRect.width;
          console.log('ResizeObserver detected width:', width);
          if (width > 0) {
            setContainerWidth(width);
          }
        }
      });
    });
    
    resizeObserver.observe(containerRef.current);
    
    // Measure immediately
    updateWidth();
    
    // Retry after short delays to catch parent layout
    const timeouts = [
      setTimeout(updateWidth, 0),
      setTimeout(updateWidth, 50),
      setTimeout(updateWidth, 100),
      setTimeout(updateWidth, 200),
    ];
    
    // Also listen to window resize
    window.addEventListener('resize', updateWidth);
    
    return () => {
      // Clean up animation frame
      if (rafId !== null) {
        cancelAnimationFrame(rafId);
      }
      resizeObserver.disconnect();
      timeouts.forEach(clearTimeout);
      window.removeEventListener('resize', updateWidth);
    };
  }, [data.length, selectedProjectId]); // Re-init when timeline appears or project switches

  // Calculate current step based on dates
  const getCurrentStep = () => {
    const today = moment();
    // const today = moment("2023-05-01"); // Mock May date for testing
    let completedSteps = 0;
    
    console.log('=== getCurrentStep Debug ===');
    console.log('Today:', today, 'ISO:', today.toISOString());
    
    for (let i = 0; i < data.length; i++) {
      const reviewDate = moment(data[i].date);
      const isCompleted = reviewDate.isSameOrBefore(today, 'day');
      console.log(`Review ${i} (${data[i].phase}):`, {
        dateString: data[i].date,
        reviewDate: reviewDate.toDate(),
        reviewDateISO: reviewDate.toISOString(),
        isCompleted,
        comparison: `${reviewDate.valueOf()} <= ${today.valueOf()}`
      });
      
      if (isCompleted) {
        completedSteps = i + 1;
      }
    }
    
    console.log('Completed Steps:', completedSteps);
    console.log('=== End Debug ===');
    
    return completedSteps;
  };
  
  // Calculate progress bar width based on container width
  const GAP_WIDTH = 26; // Gap between cards
  const cardWidth = containerWidth * 0.25 - GAP_WIDTH * 3 / 4; // got 3 gaps
  const progressWidth = (data.length * cardWidth) + ((data.length - 1) * GAP_WIDTH);
  // Stop at center of the current step's card
  const currentStep = getCurrentStep();
  console.log('Current Step:', currentStep);
  
  // Check if last item is completed (all items are done, no future cards)
  const isLastItemCompleted = currentStep === data.length;
  
  // If last item is completed, extend to full width; otherwise stop at center of current card
  const activeWidth = currentStep > 0 
    ? (isLastItemCompleted 
        ? progressWidth  // Extend fully to the end when all items are completed
        : ((currentStep - 1) * (cardWidth + GAP_WIDTH)) + (cardWidth / 2))  // Stop at center of current card
    : 0;
  
  console.log('Progress Bar Calculation:', {
    containerWidth,
    cardWidth,
    progressWidth,
    currentStep,
    activeWidth,
    percentage: progressWidth > 0 ? ((activeWidth / progressWidth) * 100).toFixed(2) + '%' : '0%'
  });

  // Reset one-time auto-scroll when switching to another project/timeline
  useEffect(() => {
    hasAutoScrolledRef.current = false;
  }, [selectedProjectId, data.length]);

  // Auto-scroll once after initial layout is ready.
  // Do not re-trigger on every data mutation (e.g., typing in TinyEditor).
  useEffect(() => {
    if (hasAutoScrolledRef.current) {
      return;
    }
    if (!containerRef.current || data.length === 0 || containerWidth <= 0 || isEditing) {
      return;
    }

    const prevStep = currentStep - 1;
    // Calculate scroll position to show current step
    const scrollTo = Math.max(
      0,
      (prevStep - 1) * (cardWidth + GAP_WIDTH),
    );

    containerRef.current.scrollTo({
      left: scrollTo,
      behavior: 'smooth',
    });
    hasAutoScrolledRef.current = true;
  }, [containerWidth, data.length, currentStep, cardWidth, isEditing, selectedProjectId]);

  // Check if there's no data
  if (!data || data.length === 0) {
    return (
      <div className="dbs-quarterly-review-empty">
        <p className="dbs-no-data-text">No data available. Currently, there is no data from the GBM system.</p>
      </div>
    );
  }

  return (
    <div className="dbs-quarterly-review" ref={containerRef}>
      {/* 进度条 */}
      <div className="dbs-progress-bar"style={{ width: progressWidth }}>
        <div className="dbs-progress-line">
          <div className="dbs-progress-line-active" style={{ width: activeWidth }}></div>
        </div>
      </div>

      {/* 卡片容器 */}
      <div className="dbs-cards-container">
        {data.map((review, index) => {
          const statusInfo = getStatusInfo(review.status);
          
          // Determine pass/completed class based on date
          const today = moment().startOf('day');
          
          const reviewDate = moment(review.date).startOf('day');
          
          const isBeforeToday = reviewDate.isBefore(today, 'day');
          
          // Check if this is the last card that has passed (completed)
          const nextReview = data[index + 1];
          const isCompleted =
            isBeforeToday &&
            (!nextReview ||
              !moment(nextReview.date).startOf('day').isBefore(today, 'day'));
          
          return (
          <div key={review.id} className={`dbs-review-card${isCompleted ? ' pass' : isBeforeToday ? ' completed' : ''}`}>
            <h3 className="dbs-review-title">{review.phase}</h3>
            <p className="dbs-review-date">{review.date}</p>
            {review.status && review.status !== '0' && statusInfo.label && (
              <div className={`dbs-status-badge ${statusInfo.className}`}>
                {statusInfo.label}
              </div>
            )}
            
            {/* Description and Files Wrapper */}
            <div className="dbs-review-content-wrapper">
              {/* Description - Editable in edit mode */}
              {isEditing ? (
                <TinyEditor
                  value={editValues[review.id] !== undefined ? editValues[review.id] : review.description}
                  getEditorContent={(content) => handleDescriptionChange(review.id, content)}
                  placeholder="Enter description"
                  isShowToolbar={true}
                  minHeight={300}
                  maxContentHeight={300}
                  isMaxHeightScroll={true}
                  synchronize={true}
                />
              ) : (
                review.description && renderRichText(review.description, 'dbs-review-description')
              )}
              {/* Files section - Different rendering for edit/view mode */}
              {isEditing ? (
                <>
                  {/* Upload button with built-in file list, rename, and delete */}
                  <UploadFileList
                    className="dbs-styled-upload-file-list"
                    uniqueId={`review-${review.id}`}
                    uploadFolderName="mgmt/publishing/project/publishing_reviews"
                    defaultList={review.files as any}
                    onResult={(files: any) => handleFilesUpload(review.id, files)}
                    showFileList={true}
                    fileListPosition="bottom"
                    buttonTips=""
                    autoResizeInput={true}
                    includeSizeInResult={false}
                  />
                </>
              ) : (
                <>
                  {/* View Mode - COSFileList with preview/share/download */}
                  {review.files.length > 0 && (
                    <COSFileList
                      className="dbs-styled-cos-file-list"
                      domain="mgmt/publishing/project/publishing_reviews"
                      cosConfigDomain="mgmt/gbm,mgmt/publishing/project/publishing_reviews"
                      urlSearchParams={selectedProjectId ? { item_id: selectedProjectId } : {}}
                      logKeys={{
                        previewLogKey: 'publishing_review_preview',
                        downloadLogKey: 'publishing_review_download',
                        shareLogKey: 'publishing_review_share',
                      }}
                      autoWrap={false}
                      files={review.files.map(file => {
                        // console.log('Original file object:', file);
                        
                        // Helper function to split filename and extension
                        const splitFileName = (fileName?: string) => {
                          if (!fileName || typeof fileName !== 'string') return { name: '', format: '' };
                          const lastDotIndex = fileName.lastIndexOf('.');
                          if (lastDotIndex <= 0) {
                            return { name: fileName, format: '' };
                          }
                          return {
                            name: fileName.substring(0, lastDotIndex),
                            format: fileName.substring(lastDotIndex + 1) // Without the dot
                          };
                        };

                        // Derive a safe file name: prefer provided name, else fallback to basename(path)
                        const rawName = (typeof file.name === 'string' && file.name)
                          || (typeof file.path === 'string' && file.path.split('/').pop())
                          || '';

                        // Always split the name to remove extension, even if format exists
                        // because UploadFileList might include extension in both name and format
                        const { name: fileNameWithoutExt, format: extractedFormat } = splitFileName(rawName);
                        
                        // Use existing format if available, otherwise use extracted format
                        const fileExtension = file.format || extractedFormat;

                        // console.log('Split result:', { fileNameWithoutExt, fileExtension, originalName: file.name, originalFormat: file.format });

                        // Ensure format doesn't have a dot at the beginning
                        const cleanFormat = (fileExtension || '').startsWith('.') ? fileExtension.substring(1) : (fileExtension || '');

                        const transformedFile = {
                          ...file,
                          name: fileNameWithoutExt || rawName,
                          format: cleanFormat, // Without the dot
                          // Convert size to number if it's a string
                          size: typeof file.size === 'string' ? parseInt(file.size) : file.size || 0,
                        };
                        
                        // console.log('Transformed file for COSFileList:', transformedFile);
                        
                        return transformedFile;
                      })}
                    />
                  )}
                </>
              )}
            </div>
            {/* 图标展示 */}
            <div className="dbs-review-location"></div>
            <div className="dbs-review-arrow"></div>
          </div>
          );
        })}
      </div>
    </div>
  );
};

export default QuarterlyReviewProgress;